import type { MantineNumberSize } from '@mantine/core';
import {
	Center,
	createStyles,
	Group,
	Image,
	Loader,
	Stack,
} from '@mantine/core';
import type { IconNames } from '@repo/foundations';
import { Button, Icon, Text } from '@repo/foundations';
import { Illustration } from '../Illustration';
import type { IllustrationNames } from '../Illustration/constants';

export type EmptyStateSize = 'sm' | 'lg';

export interface ButtonDetails {
	name: string;
	action: () => void;
	size: 'sm' | 'md' | 'lg';
	isPrimary: boolean;
	loading?: boolean;
	leftIconName?: IconNames;
	rightIconName?: IconNames;
	requiresUseButtonPermission?: boolean;
}

export interface EmptyStateProps {
	title: string;
	description: string;
	buttons?: ButtonDetails[];
	includeGoBack: boolean;
	iconName?: IconNames;
	illustrationName?: IllustrationNames;
	imgSrc?: string;
	size: EmptyStateSize;
	stateHeight?: number | string;
	imgWidth?: number | string;
	descWidth?: MantineNumberSize;
	className?: string;
	classNames?: Partial<Record<'root' | 'image', string>>;
	withActions?: JSX.Element | null;
	width?: MantineNumberSize;
	handleBackButtonClick?: () => void;
	withBorder?: boolean;
	loading?: boolean;
	hasUseButtonPermission?: boolean;
}

const useStyles = createStyles(
	(
		theme,
		{
			stateHeight,
			width,
			withBorder,
		}: {
			stateHeight?: number | string;
			width?: MantineNumberSize;
			withBorder?: boolean;
		}
	) => ({
		root: {
			marginBottom: 24,
			height: stateHeight,
			width,
			margin: 'auto',
			...(withBorder && {
				border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
				borderRadius: theme.other.borderRadius.md,
			}),
		},
		box: {
			marginBottom: 16,
		},
		gameLink: {
			fontSize: 10,
		},
	})
);

function EmptyState({
	className,
	classNames,
	title,
	description,
	buttons = [],
	includeGoBack,
	iconName,
	illustrationName,
	size,
	imgSrc,
	imgWidth = 120,
	descWidth,
	stateHeight,
	withActions,
	width,
	loading,
	handleBackButtonClick,
	withBorder,
	hasUseButtonPermission,
}: EmptyStateProps): JSX.Element {
	const { classes, cx, theme } = useStyles({ stateHeight, width, withBorder });

	// Replace trailing periods in the description with nothing if it's not three periods.
	let formattedDescription = description;
	if (description.endsWith('.') && !description.endsWith('...')) {
		formattedDescription = description.slice(0, -1);
	}

	// Filter out buttons that require permission if user does not have permission
	const filteredButtons = buttons.filter(
		(button) => !(button.requiresUseButtonPermission && !hasUseButtonPermission)
	);

	return (
		<Center
			data-testid="empty-state"
			className={cx(classes.root, className, classNames?.root)}
		>
			<Stack align="center" py={theme.spacing.lg}>
				{iconName && (
					<Icon name={iconName} size="lg" color="icon/secondary/default" />
				)}
				{illustrationName && <Illustration name={illustrationName} />}
				{imgSrc && (
					<Image src={imgSrc} width={imgWidth} className={classNames?.image} />
				)}
				<Stack maw={descWidth ?? theme.other.space[90]} spacing="xs">
					<Text weight="bold" size={size === 'lg' ? 'lg' : 'sm'} ta="center">
						{title}
					</Text>
					<Text color="text/secondary/default" size="sm" ta="center">
						{formattedDescription}
					</Text>
				</Stack>
				{withActions && <Group position="center">{withActions}</Group>}
				{(includeGoBack || filteredButtons.length > 0) && (
					<Group position="center" noWrap>
						{filteredButtons.map((button) => (
							<Button
								key={button.name}
								onClick={button.action}
								size={button.size}
								variant={button.isPrimary ? 'primary' : 'default'}
								loading={button.loading ?? false}
								leftIconName={button.leftIconName}
								rightIconName={button.rightIconName}
							>
								{button.name}
							</Button>
						))}
						{includeGoBack &&
							filteredButtons.every((button) => button.name !== 'Go back') &&
							!!handleBackButtonClick && (
								<Button onClick={handleBackButtonClick} variant="default">
									Go back
								</Button>
							)}
					</Group>
				)}
				{loading && <Loader size="sm" />}
			</Stack>
		</Center>
	);
}

export { EmptyState };
